import { RcIcon } from '@/components/atoms/RcIcon'
import { RcSuspense } from '@/components/atoms/RcSuspense'
import { Redirect } from '@/components/pages/CMS/dynamic/Redirect'
import { useLocalStorage } from '@/core/hooks/useLocalStorage'
import { useIsMdUp, useIsMobile } from '@/core/hooks/useMediaQueries'
import { useQuery } from '@/core/hooks/useQuery'
import { scrollIntoViewWithOffset } from '@/core/utils'
import { calcElevationString } from '@/style/palette'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { find, isEmpty, isUndefined } from 'lodash-es'
import { useParams, usePathname } from 'next/navigation'
import { forwardRef, useEffect, useRef, useState } from 'react'
import { PageNavigator, PageNavigatorOption } from './PageNavigator'

export interface PageNavSwitchProps {
  width?: number
  height?: number
  options: PageNavSwitchOption[]
  scrollTo?: number
  extraRoutes?: React.ReactNode
  alwaysShowNav?: boolean
  disablePin?: boolean
  titleSection?: boolean

  title?: React.ReactNode
}

export interface PageNavSwitchOption extends PageNavigatorOption {
  fill?: boolean
  component?: () => React.ReactNode
}

export const PageNavSwitch = ({
  width,
  options,
  scrollTo,
  extraRoutes,
  alwaysShowNav,
  titleSection,
  title,
  disablePin = true
}: PageNavSwitchProps) => {
  const ref = useRef<HTMLElement>()
  const pathname = usePathname()
  const isMdUp = useIsMdUp()
  const isMobileDevice = useIsMobile()
  const [pinned] = useLocalStorage({
    key: disablePin ? 'pin-t3-disabled' : 'pin-t3-nav',
    defaultValue: titleSection
  })

  const [expanded, setExpanded] = useState(titleSection ?? false)
  const DRAWER_WIDTH_OPEN = 275
  const DRAWER_WIDTH_CLOSED = width || 65

  const q = useQuery()
  const isScrollTo = !!q?.get('scrollTo')
  const scrollToOffset = isUndefined(scrollTo) ? (isMdUp ? 35 : 50) : scrollTo
  useEffect(() => {
    if (!isScrollTo) return
    scrollIntoViewWithOffset(ref.current, scrollToOffset)
  }, [ref, isScrollTo, scrollToOffset, pathname])

  const { slug } = useParams()

  if (isEmpty(options)) return null

  const augmented = options.map(it => ({
    ...it,
    action: () => {
      it.action?.()
      setExpanded(false)
      scrollIntoViewWithOffset(ref.current, scrollToOffset)
    }
  }))

  const firstOption = find(augmented, it => !!it.to)
  if (!slug?.[0]) {
    return <Redirect path={`${pathname}/${firstOption?.to}`} replace />
  }

  const selected = options.find(it => it.to === slug?.[0])
  const route = selected?.component?.() || extraRoutes

  if (isEmpty(augmented)) return null
  if (augmented.length === 1 && !alwaysShowNav)
    return (
      <PageNavSwitchPage skipMarginTop fill={augmented[0].fill}>
        {route}
      </PageNavSwitchPage>
    )

  return (
    <Stack
      direction='row'
      // ref={ref}
      minHeight={theme => `calc(100dvh  - ${theme.spacing(12)})`}
      alignItems='stretch'
    >
      {/* Left Column */}
      <Stack
        spacing={3}
        onMouseEnter={ev => !isMobileDevice && setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
        sx={theme => ({
          zIndex: theme.zIndex.drawer,
          width:
            pinned && !isMobileDevice ? DRAWER_WIDTH_OPEN : DRAWER_WIDTH_CLOSED,
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10
        })}
      >
        {titleSection ? title : null}
        {/* Expandable Drawer */}
        <Paper
          elevation={0}
          sx={theme => ({
            backgroundColor: theme.palette.background.paper,
            backgroundImage: calcElevationString(theme.palette.mode, [1]),
            borderTopLeftRadius: isMdUp ? theme.shape.borderRadius : 0,
            borderBottomLeftRadius: isMdUp ? theme.shape.borderRadius : 0,
            borderRight: `1px solid ${theme.palette.divider}`,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            width: `${
              expanded || (pinned && !isMobileDevice)
                ? DRAWER_WIDTH_OPEN
                : DRAWER_WIDTH_CLOSED
            }px`,
            p: 1,
            height: '100%',
            transition: expanded
              ? theme.transitions.create(['width'], {
                  duration: theme.transitions.duration.enteringScreen
                })
              : undefined
          })}
        >
          {/* Stickied Nav Options */}
          <Box
            sx={{
              overflowX: 'hidden',
              position: 'sticky',
              top: 0
            }}
          >
            <DesktopNavigation
              disablePin={disablePin}
              options={augmented}
              expanded={expanded}
              setExpanded={setExpanded}
            />
          </Box>
        </Paper>
      </Stack>
      {/* Right Column */}
      <Box
        width={`calc(100%  - ${
          pinned && !isMobileDevice ? DRAWER_WIDTH_OPEN : DRAWER_WIDTH_CLOSED
        }px)`}
      >
        <PageNavSwitchPage fill={selected?.fill || route === extraRoutes}>
          <Box ref={ref} />
          {route}
        </PageNavSwitchPage>
      </Box>
    </Stack>
  )
}

const PageNavSwitchPage = forwardRef(function PageNavSwitchPage(
  {
    fill,
    skipMarginTop,
    children
  }: { fill?: boolean; skipMarginTop?: boolean; children?: React.ReactNode },
  ref: any
) {
  return (
    <RcSuspense borderRadius={'0 8px 8px 0'}>
      <Box
        ref={ref}
        sx={theme => ({
          position: 'relative',
          border: 'none',
          marginTop: fill || skipMarginTop ? 0 : 5,
          marginBottom: 0,
          borderRadius: {
            xs: 0,
            md: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 `
          },
          height: '100%',
          width: '100%',
          paddingTop: skipMarginTop && !fill ? 5 : 0,
          paddingRight: fill ? 0 : 5,
          paddingLeft: fill ? 0 : 5,
          paddingBottom: fill ? 0 : 5
        })}
      >
        {children}
      </Box>
    </RcSuspense>
  )
})

const DesktopNavigation = ({
  options,
  expanded,
  setExpanded,
  disablePin
}: PageNavSwitchProps & {
  expanded: boolean
  setExpanded: (value: boolean | ((val: boolean) => boolean)) => void
}) => {
  const isMobile = useIsMobile()
  const [pinned, setPinned] = useLocalStorage({
    key: 'pin-t3-nav',
    defaultValue: false
  })

  const pin =
    disablePin && !isMobile
      ? []
      : [
          {
            action: () => {
              setExpanded(e => !e)
              if (!isMobile) {
                setPinned(!pinned)
              }
            },
            icon: (
              <RcIcon
                icon={
                  isMobile
                    ? expanded
                      ? ['fal', 'chevron-double-left']
                      : ['fal', 'chevron-double-right']
                    : expanded
                      ? pinned
                        ? ['fal', 'chevron-double-left']
                        : ['fal', 'chevron-double-right']
                      : ['fal', 'bars']
                }
              />
            ),
            text: isMobile ? ' ' : pinned ? 'Collapse' : 'Lock',
            textHoverOnly: true
          }
        ]

  return (
    <PageNavigator
      onMouseEnter={() => !isMobile && setExpanded(true)}
      options={[...pin, ...options]}
      variant='vertical'
      skipScroll
    />
  )
}
